import type React from 'react';
import {
  type FieldValues,
  type UseFormStateReturn,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useZodContext } from './contexts/zodContext';

/**
 * Function to determine when form part should be shown
 * @param state - form state
 */
type ShowWhenFn = <T extends FieldValues>(
  state: UseFormStateReturn<T> & { values: T },
) => boolean;

export type FormPartProps = {
  /**
   * Function or value to determine when form part should be shown
   */
  showWhen?: boolean | ShowWhenFn;

  /**
   * Field name to check if it exists in the schema
   * If provided, the component will check if the field exists in the schema
   */
  schemaField?: string;

  /**
   * Children to render
   */
  children: React.ReactNode;
};

/**
 * Form part component
 * This component is used to determine when a part of the form should be shown
 * This component must be used inside a Form component
 *
 * @example
 * ```tsx
 * <FormPart showWhen={state => state.isDirty}>
 *   <Alert>Form is dirty</Alert>
 * </FormPart>
 * ```
 *
 * @example
 * ```tsx
 * <TextField name="name" />
 * <FormPart showWhen={state => state.values.name === 'John'}>
 *   <Alert>Helo John!</Alert>
 * </FormPart>
 * ```
 *
 * @example
 * ```tsx
 * <FormPart schemaField="email">
 *   <TextField name="email" />
 * </FormPart>
 * ```
 *
 * @example
 * ```tsx
 * <FormPart schemaField="address" showWhen={state => state.values.hasAddress}>
 *   <TextField name="address" />
 * </FormPart>
 * ```
 *
 * @param showWhen - function or value to determine when form part should be shown
 * @param schemaField - field name to check if it exists in the schema
 * @param children - children to render
 */
export const FormPart: React.FC<FormPartProps> = ({
  showWhen = true,
  schemaField,
  children,
}) => {
  const state = useFormState();
  const values = useWatch();
  const formState = { ...state, values };
  const { hasField } = useZodContext();

  // Check if the field exists in the schema if a schemaField is provided
  const fieldExists = schemaField ? hasField(schemaField, values) : true;

  // Only show if both the showWhen condition is met and the field exists in the schema
  const showWhenCondition =
    typeof showWhen === 'function' ? showWhen(formState) : showWhen;
  const show = showWhenCondition && fieldExists;

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return show ? children : null;
};
